/**
 * Centralized environment configuration.
 *
 * All environment variables are resolved once at module load and exposed as
 * typed constants. Service files import from here instead of reading
 * `import.meta.env` directly. This gives us:
 *   - A single place to define defaults and per-environment overrides
 *   - Type safety (no `string | undefined` scattered everywhere)
 *   - Easy to add a staging tier later
 *   - Vite tree-shakes unused branches in production builds
 */

// ── Helpers ──────────────────────────────────────────────────────────

type Environment = 'development' | 'staging' | 'production';

function resolveEnvironment(): Environment {
  // Explicit override takes priority (useful for staging)
  const explicit = import.meta.env.VITE_APP_ENV;
  if (explicit === 'staging') return 'staging';
  if (explicit === 'production' || import.meta.env.PROD) return 'production';
  return 'development';
}

/** Read a VITE_ variable, returning `undefined` when blank/missing. */
function envStr(key: string): string | undefined {
  const val = import.meta.env[key] as string | undefined;
  return val && val.trim() !== '' ? val : undefined;
}

// ── Resolved environment ──────────────────────────────────────────────

export const APP_ENV = resolveEnvironment();
export const IS_DEV = APP_ENV === 'development';
/** @internal Test seam: resolveEnvironment.test pins the staging label. */
export const IS_STAGING = APP_ENV === 'staging';
export const IS_PROD = APP_ENV === 'production';

// ── Service URLs ──────────────────────────────────────────────────────

const DEFAULTS = {
  development: {
    API_URL: 'http://localhost:3001',
    API_BASE_URL: 'http://localhost:3001/api',
    AGENT_SERVICE_URL: 'http://localhost:8001',
    AGENT_SERVICE_WS_URL: 'ws://localhost:8001',
  },
  staging: {
    API_URL: '',
    API_BASE_URL: '',
    AGENT_SERVICE_URL: '',
    AGENT_SERVICE_WS_URL: '',
  },
  production: {
    API_URL: '',
    API_BASE_URL: '',
    AGENT_SERVICE_URL: '',
    AGENT_SERVICE_WS_URL: '',
  },
} as const;

/** Node.js backend base URL (WebSocket connections). */
export const API_URL = envStr('VITE_API_URL') || DEFAULTS[APP_ENV].API_URL;

/** Node.js backend REST API (axios baseURL). */
export const API_BASE_URL = envStr('VITE_API_BASE_URL') || DEFAULTS[APP_ENV].API_BASE_URL;

/** Python agent service (Bird chat). */
export const AGENT_SERVICE_URL =
  envStr('VITE_AGENT_SERVICE_URL') || DEFAULTS[APP_ENV].AGENT_SERVICE_URL;

/** Python agent service WebSocket URL (Bird live mode). */
export const AGENT_SERVICE_WS_URL =
  envStr('VITE_AGENT_SERVICE_WS_URL') || DEFAULTS[APP_ENV].AGENT_SERVICE_WS_URL;

// ── Firebase ──────────────────────────────────────────────────────────

export const FIREBASE = {
  apiKey: envStr('VITE_FIREBASE_API_KEY') || '',
  authDomain: envStr('VITE_FIREBASE_AUTH_DOMAIN') || '',
  projectId: envStr('VITE_FIREBASE_PROJECT_ID') || '',
  storageBucket: envStr('VITE_FIREBASE_STORAGE_BUCKET') || '',
  messagingSenderId: envStr('VITE_FIREBASE_MESSAGING_SENDER_ID') || '',
  appId: envStr('VITE_FIREBASE_APP_ID') || '',
  measurementId: envStr('VITE_FIREBASE_MEASUREMENT_ID') || '',
} as const;

// ── Feature flags (static, compile-time) ─────────────────────────────

export const FEATURES = {
  devTools: envStr('VITE_ENABLE_DEV_TOOLS') === 'true',
} as const;

// ── Third-party keys ─────────────────────────────────────────────────

export const TURNSTILE_SITE_KEY = envStr('VITE_TURNSTILE_SITE_KEY') || '';

// ── PostHog Analytics ───────────────────────────────────────────────
export const POSTHOG_KEY = envStr('VITE_POSTHOG_KEY') || '';

// GA4, for the app's pre-authentication pages only — see utils/ga4.ts, which
// owns the allowlist that keeps this out of the PHI boundary. Same property as
// the marketing site so signup attribution is one funnel.
export const GA4_MEASUREMENT_ID = envStr('VITE_GA4_MEASUREMENT_ID') || '';
export const POSTHOG_HOST = envStr('VITE_POSTHOG_HOST') || 'https://us.i.posthog.com';

// ── Analytics Debug Mode ────────────────────────────────────────────
// Set VITE_ANALYTICS_DEBUG=true in .env to enable PostHog in dev with console logging.
// Events are logged to the browser console and sent to PostHog (if key is set).
export const ANALYTICS_DEBUG = envStr('VITE_ANALYTICS_DEBUG') === 'true';

// ── Required-vars runtime backstop ──────────────────────────────────
//
// CI's `vite-env-wiring-check` job is the primary guard against missing
// `VITE_*` vars in the build env block. This block is the backstop: if
// CI is somehow bypassed (force-merge, manual deploy, broken matcher),
// we still surface the missing var loudly at runtime so the gap shows
// up in Sentry/PostHog/console instead of silently disabling features.
//
// Only runs in production builds. In dev the missing var usually means
// the developer hasn't filled `.env`; the warning is on but at info
// level. Source of truth: `required-vite-vars.json`.
import REQUIRED_VITE_VARS from './required-vite-vars.json';

const _missingRequired = REQUIRED_VITE_VARS.filter((k) => !envStr(k));
if (_missingRequired.length > 0) {
  const message =
    `[env] ${_missingRequired.length} required VITE_* var(s) missing from the build: ` +
    _missingRequired.join(', ') +
    '. See .claude/rules/env-vars.md.';
  if (IS_PROD) {
    // eslint-disable-next-line no-console -- production surface for Sentry/PostHog
    console.error(message);
  } else {
    // eslint-disable-next-line no-console -- dev-time hint
    console.info(message);
  }
}
